import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import useSnackbar from '../../../../hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../contexts/auth';
import { ProxyLoginContext } from '../../../../contexts/ProxyLoginContext';
import { useAuthRedirect } from '../../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import { isOperatorStaff } from '../../../../utils/permission';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import {
  Box,
  Button,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  gray10,
  gray100,
  gray20,
  gray40,
  gray80,
  red50,
  white,
} from '../../../../constants/colors';
import { boldXxl, regularSm, spHeaderTitle } from '../../../../constants/location';
import MetaTitle from '../../../utils/MetaTitle';
import SideBar from '../../../uiParts/menu/SideBar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import DeleteModal from '../../../uiParts/modal/DeleteModal';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import dayjs from 'dayjs';
import {
  listAllMasterDatasets,
  deleteMasterDatasetPhysically,
} from '../../../../api/firebase/function/handleMasterAdmin';
import { MasterDatasetRow, masterFirestoreState } from '../../../../types/masterAdmin';

/**
 * 運営事務向け: 全企業のマスタ一覧と物理削除（#3254）
 *
 * Cloud SQL の master_datasets を主として表示し、Firestore の状態を突き合わせる。
 * Firestore に対応ドキュメントが無い「登録失敗」も表示されるため、
 * 登録途中で失敗して Cloud SQL に残った無駄な容量を検出できる。
 */

/** 状態カラムの補足説明 */
const STATE_HELP_TEXT = `企業がマスタを削除しても、データベース（Cloud SQL）からは消えず、容量を使い続けます。この画面では、その残ったデータを完全に削除できます。

【使用中】企業が現在使っています。物理削除できません。

【企業側で削除済み（DBに残存）】企業のマスタ画面からは削除されて見えなくなっていますが、データベースには残っています。物理削除できます。

【登録失敗（DBに残存）】マスタ登録の途中で失敗し、データベースにだけ残ってしまったものです。どこからも参照されていません。物理削除できます。

【完全削除済み】この画面で物理削除を実行済みです。データベースからは消えており、容量は使っていません。何を削除したかの記録として表示しています。`;

const AdminAllMasters = (): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, isLoading } = useContext(AuthContext);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 代理モード中はアクセス禁止
  useProxyAccessControl();
  const { startProxyLogin, isProxyLogin } = useContext(ProxyLoginContext);

  const [showData, setShowData] = useState<MasterDatasetRow[]>([]);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MasterDatasetRow | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState<boolean>(false);

  const styles = {
    deleteButton: {
      ...regularSm,
      border: '1px solid' + red50,
      borderRadius: '4px',
      color: red50,
      minWidth: 'auto',
      padding: '4px 12px',
      whiteSpace: 'nowrap',
    },
    fileNameText: {
      display: 'inline-block',
      maxWidth: '260px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    companyNameText: {
      display: 'inline-block',
      maxWidth: '180px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    headerTitle: {
      ...boldXxl,
      color: gray100,
      '@media screen and (max-width:960px)': {
        ...spHeaderTitle,
      },
    },
    icon: {
      height: '56px',
      margin: '80px 0 24px 0',
      width: '56px',
      '@media screen and (max-width: 600px)': {
        margin: '24px 0 24px 0',
      },
    },
    stateBadge: {
      ...regularSm,
      borderRadius: '4px',
      display: 'inline-block',
      padding: '2px 8px',
      whiteSpace: 'nowrap',
    },
    summaryNote: {
      ...regularSm,
      color: gray80,
      padding: '0 0 16px',
      textAlign: 'left',
    },
    summaryRow: {
      alignItems: 'center',
      display: 'flex',
      gap: '16px',
      justifyContent: 'space-between',
      padding: '0 0 8px',
    },
    summaryText: {
      ...regularSm,
      color: gray100,
      fontWeight: 500,
      textAlign: 'left',
    },
    bulkDeleteButton: {
      ...regularSm,
      border: '1px solid' + red50,
      borderRadius: '4px',
      color: red50,
      flexShrink: 0,
      padding: '6px 16px',
      whiteSpace: 'nowrap',
    },
    helpIcon: {
      color: gray80,
      cursor: 'help',
      fontSize: '16px',
      marginLeft: '4px',
      verticalAlign: 'middle',
    },
    table: {
      minWidth: '1200px',
      width: '100%',
    },
    /** 行内の操作ボタンの並び（#3308） */
    rowActions: {
      alignItems: 'center',
      display: 'inline-flex',
      gap: '8px',
    },
    proxyLoginButton: {
      ...regularSm,
      border: '1px solid' + gray80,
      borderRadius: '4px',
      color: gray80,
      minWidth: 'auto',
      padding: '4px 12px',
      whiteSpace: 'nowrap',
    },
    tableDataRow: {
      height: '48px',
    },
    tableDataText: {
      ...regularSm,
      color: gray100,
      padding: '0 16px',
      whiteSpace: 'nowrap',
    },
    tableHeadRow: {
      height: '48px',
    },
    tableHeadText: {
      ...regularSm,
      color: gray80,
      padding: '0 16px',
      whiteSpace: 'nowrap',
    },
    tableSection: {
      backgroundColor: white,
      border: '1px solid' + gray40,
      borderRadius: '4px',
      boxSizing: 'border-box',
      margin: '0 32px',
      minHeight: '560px',
      padding: '16px 24px 56px',
      width: 'calc(100% - 64px)',
      '@media screen and (max-width:960px)': {
        border: 'none',
        borderRadius: 0,
        margin: 0,
        width: '100%',
      },
    },
    tableWrapper: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      width: '100%',
    },
    titleAndStatusWrapper: {
      display: 'flex',
      flexDirection: 'row',
      flexGrow: 1,
      justifyContent: 'space-between',
      marginRight: '18px',
      minWidth: 0,
      '@media screen and (max-width: 960px)': {
        fontSize: '12px',
        marginRight: '0',
      },
    },
  };

  useEffect(() => {
    if (isLoading) return;
    if (user === null) return;
    // 運営スタッフ（role=10,11）以外はアクセス不可
    if (!isOperatorStaff(user.role)) {
      navigate('/home');
      return;
    }
    // 代理ログイン中は運営向け画面を開かせない（#3308）。
    // URL を直接叩くと、代理モード中バナーを出したまま全企業のマスタが見えてしまうため。
    // 代理を終了してから開き直してもらう。
    if (isProxyLogin) {
      navigate('/home');
    }
  }, [user, isLoading, navigate, isProxyLogin]);

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await listAllMasterDatasets();
      setShowData(response.datasets);
      setShowDataFlg(response.datasets.length > 0);
    } catch {
      setErrorText('マスタ一覧の取得に失敗しました');
      setSnackbarOpen(true);
      setShowDataFlg(false);
    } finally {
      setLoading(false);
    }
  }, [setErrorText, setSnackbarOpen]);

  useEffect(() => {
    if (isLoading) return;
    if (user === null) return;
    if (!isOperatorStaff(user.role)) return;
    void fetchData();
  }, [user, isLoading, fetchData]);

  const handleDelete = async (): Promise<void> => {
    const target = deleteTarget;
    if (!target) return;
    try {
      setLoading(true);
      const result = await deleteMasterDatasetPhysically({
        master_dataset_id: target.master_dataset_id,
      });
      setDeleteTarget(null);
      setInfoText(`${result.deleted_records.toLocaleString()}件のデータを削除しました`);
      setSnackbarOpen(true);
      await fetchData();
    } catch {
      setErrorText('物理削除に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 削除可能なマスタをまとめて物理削除する
   * 大量削除で Cloud SQL に負荷をかけないよう、1件ずつ順に実行する
   */
  const handleBulkDelete = async (): Promise<void> => {
    const targets = showData.filter(data => data.deletable);
    if (targets.length === 0) return;
    try {
      setLoading(true);
      let deletedRecords = 0;
      let failedCount = 0;
      for (const target of targets) {
        try {
          const result = await deleteMasterDatasetPhysically({
            master_dataset_id: target.master_dataset_id,
          });
          deletedRecords += result.deleted_records;
        } catch {
          // 1件失敗しても残りを続行し、最後にまとめて件数を知らせる
          failedCount += 1;
        }
      }
      setBulkDeleteOpen(false);
      if (failedCount > 0) {
        setErrorText(
          `${targets.length - failedCount}件を削除しました（${failedCount}件は失敗しました）`,
        );
      } else {
        setInfoText(
          `${targets.length}件のマスタ（${deletedRecords.toLocaleString()}件のデータ）を削除しました`,
        );
      }
      setSnackbarOpen(true);
      await fetchData();
    } finally {
      setLoading(false);
    }
  };

  /**
   * 「代理ログイン」ボタンで、その企業に代理ログインする（#3254 / #3308）
   * 遷移先は企業ホーム。マスタ画面へ直接飛ばすと、代理先の企業情報が届く前に
   * マスタ画面側の enable_master 判定が走り /home へ弾かれるため。
   * 使用中のマスタのみ対象。削除済み・登録失敗は企業のマスタ画面に存在しないため遷移しない。
   */
  const handleProxyLogin = (data: MasterDatasetRow): void => {
    if (!user || user.role === null || !data.company_name) return;
    startProxyLogin(data.gendrive_company_id, data.company_name, user.uid, user.role);
  };

  /** Firestore 状態のラベルと色 */
  const stateLabel = (
    state: MasterDatasetRow['firestore_state'],
  ): { text: string; color: string; background: string } => {
    switch (state) {
      case masterFirestoreState.deleted:
        return { text: '企業側で削除済み（DBに残存）', color: red50, background: gray20 };
      case masterFirestoreState.orphan:
        return { text: '登録失敗（DBに残存）', color: red50, background: gray20 };
      case masterFirestoreState.purged:
        return { text: '完全削除済み', color: gray80, background: gray20 };
      default:
        return { text: '使用中', color: gray100, background: white };
    }
  };

  // 物理削除可能な容量の目安を出す（運営事務が整理の要否を判断できるようにする）
  const deletableRecordCount = showData
    .filter(data => data.deletable)
    .reduce((sum, data) => sum + data.record_count, 0);
  // 完全削除済みは Cloud SQL に存在しないため、現存データの集計には含めない
  const totalRecordCount = showData
    .filter(data => data.firestore_state !== masterFirestoreState.purged)
    .reduce((sum, data) => sum + data.record_count, 0);
  const deletableCount = showData.filter(data => data.deletable).length;
  // 完全削除済みは Cloud SQL に存在しないため、マスタ件数の集計から除く
  const activeMasterCount = showData.filter(
    data => data.firestore_state !== masterFirestoreState.purged,
  ).length;

  // 使用中 → 削除可能（DBに残存）→ 完全削除済み の順に並べる。
  // 同じ区分内は master_dataset_id の降順（新しい順）
  const stateOrder = (row: MasterDatasetRow): number => {
    if (row.firestore_state === masterFirestoreState.purged) return 2;
    return row.deletable ? 1 : 0;
  };
  const sortedData = [...showData].sort((a, b) => {
    const diff = stateOrder(a) - stateOrder(b);
    if (diff !== 0) return diff;
    return b.master_dataset_id - a.master_dataset_id;
  });

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '84px',
            },
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box sx={styles.titleAndStatusWrapper}>
            <Typography sx={styles.headerTitle}>全てのマスタ</Typography>
          </Box>
        </Box>
        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={styles.tableSection}>
            {showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : showDataFlg ? (
              <>
                <Box sx={styles.summaryRow}>
                  <Typography sx={styles.summaryText}>
                    マスタ {activeMasterCount}件 / データ {totalRecordCount.toLocaleString()}件
                    {deletableCount > 0 &&
                      `（うち削除可能 マスタ ${deletableCount}件・データ ${deletableRecordCount.toLocaleString()}件）`}
                  </Typography>
                  {deletableCount > 0 && (
                    <Button
                      sx={styles.bulkDeleteButton}
                      onClick={(): void => setBulkDeleteOpen(true)}
                    >
                      削除可能なマスタ{deletableCount}件をまとめて削除
                    </Button>
                  )}
                </Box>
                <Typography sx={styles.summaryNote}>
                  企業がマスタを削除しても、データベースからは消えず容量を使い続けます。ここでは残ったデータを完全に削除できます。
                </Typography>
                <Box sx={styles.tableWrapper}>
                  <Table sx={styles.table}>
                    <TableHead>
                      <TableRow sx={styles.tableHeadRow}>
                        <TableCell sx={styles.tableHeadText}>会社名</TableCell>
                        <TableCell sx={styles.tableHeadText}>ファイル名</TableCell>
                        <TableCell sx={styles.tableHeadText}>照合キー</TableCell>
                        <TableCell sx={styles.tableHeadText}>データ件数</TableCell>
                        <TableCell sx={styles.tableHeadText}>
                          状態
                          <Tooltip
                            title={
                              <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
                                {STATE_HELP_TEXT}
                              </Box>
                            }
                          >
                            <HelpOutlineIcon sx={styles.helpIcon} />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={styles.tableHeadText}>登録日</TableCell>
                        <TableCell sx={styles.tableHeadText} />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {sortedData.map(data => {
                        const label = stateLabel(data.firestore_state);
                        const isActive = data.firestore_state === masterFirestoreState.active;
                        return (
                          <TableRow
                            key={data.master_dataset_id}
                            sx={{
                              ...styles.tableDataRow,
                              ...(!isActive && { backgroundColor: gray10 }),
                            }}
                          >
                            {/* 会社名 */}
                            <TableCell sx={styles.tableDataText}>
                              <Tooltip title={data.gendrive_company_id}>
                                <Box component="span" sx={styles.companyNameText}>
                                  {data.company_name || data.gendrive_company_id}
                                </Box>
                              </Tooltip>
                            </TableCell>

                            {/* ファイル名 */}
                            <TableCell sx={styles.tableDataText}>
                              <Tooltip title={data.file_name || ''}>
                                <Box component="span" sx={styles.fileNameText}>
                                  {data.file_name || '-'}
                                </Box>
                              </Tooltip>
                            </TableCell>

                            {/* 照合キー */}
                            <TableCell sx={styles.tableDataText}>
                              {data.search_key || '-'}
                            </TableCell>

                            {/* データ件数。完全削除済みの行は削除したデータ件数を表す */}
                            <TableCell sx={styles.tableDataText}>
                              {data.record_count.toLocaleString()}
                            </TableCell>

                            {/* 状態 */}
                            <TableCell sx={styles.tableDataText}>
                              <Box
                                component="span"
                                sx={{
                                  ...styles.stateBadge,
                                  backgroundColor: label.background,
                                  color: label.color,
                                }}
                              >
                                {label.text}
                              </Box>
                            </TableCell>

                            {/* 登録日 */}
                            <TableCell sx={styles.tableDataText}>
                              {data.created_at ? dayjs(data.created_at).format('YYYY/MM/DD') : '-'}
                            </TableCell>

                            {/* 操作 */}
                            <TableCell sx={styles.tableDataText}>
                              <Box sx={styles.rowActions}>
                                {/* 代理ログインは行クリックではなくボタンで行う（#3308）。
                                    行のどこを押しても代理が始まると、戻すのが手間なわりに
                                    誤って押しやすいため。 */}
                                {isActive && (
                                  <Button
                                    sx={styles.proxyLoginButton}
                                    onClick={(): void => handleProxyLogin(data)}
                                  >
                                    代理ログイン
                                  </Button>
                                )}
                                {data.deletable && (
                                  <Button
                                    sx={styles.deleteButton}
                                    onClick={(): void => setDeleteTarget(data)}
                                  >
                                    物理削除
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : (
              <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                <DescriptionIcon sx={styles.icon} />
                <Typography>マスタがありません</Typography>
              </Box>
            )}
          </Box>
        </Grid2>
      </Box>

      <DeleteModal
        open={deleteTarget !== null}
        onClose={(): void => setDeleteTarget(null)}
        onDelete={(): void => {
          void handleDelete();
        }}
        title="マスタを物理削除しますか？"
        description={
          deleteTarget
            ? `${deleteTarget.file_name || '(ファイル名なし)'}（データ ${deleteTarget.record_count.toLocaleString()}件）をデータベースから完全に削除します。この操作は取り消せません。`
            : ''
        }
        loading={loading}
      />

      <DeleteModal
        open={bulkDeleteOpen}
        onClose={(): void => setBulkDeleteOpen(false)}
        onDelete={(): void => {
          void handleBulkDelete();
        }}
        title="削除可能なマスタをまとめて削除しますか？"
        description={`削除済み・登録失敗のマスタ ${deletableCount}件（データ ${deletableRecordCount.toLocaleString()}件）をデータベースから完全に削除します。この操作は取り消せません。`}
        loading={loading}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      <CustomLoading loading={loading} />
    </Box>
  );
};

export default AdminAllMasters;
